// Project Admin — Project Code Generation (PDF page 15)
import React, { useEffect, useMemo, useState } from 'react';
import { wsAPI, vendorAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  PageShell, PageHead, Card, Field, Input, Select, Tabs, Badge, ReqStatus, Note, Empty, Loading, Spin,
  useAsync, useDirectory, useCostCenters, clearCache, useSort, Th, usePaged, Pager, fmtDate, BUSINESS_UNITS, DIVISIONS, LOCATIONS, downloadCSV,
} from '../../components/ui';
import RequestDetail from '../../components/RequestDetail';
import { notify } from '../../utils/notify';

const FY = (() => { const y = new Date().getFullYear(); return [`${y - 1}–${String(y).slice(-2)}`, `${y}–${String(y + 1).slice(-2)}`, `${y + 1}–${String(y + 2).slice(-2)}`]; })();
const blank = () => ({ businessUnit: 'Water Business Unit', division: 'Engineering & Constructions', client: '', projectName: '', site: 'Jamshedpur', financialYear: FY[1], projectManagerId: '', contractReference: '', startDate: '', plannedEndDate: '', duplicateCheck: 'Enabled', costCenterMapping: 'Required', costCenter: '', budgetOwner: 'Assigned' });

export default function ProjectCodes() {
  const { user } = useAuth();
  const canManage = ['admin', 'finance', 'procurement', 'manager', 'hod'].includes(user?.role);
  const people = useDirectory();
  const costCenters = useCostCenters();
  const [tab, setTab] = useState('create');
  const [f, setF] = useState(blank());
  const [preview, setPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState('');
  const projects = useAsync(() => wsAPI.projects({ limit: 1000 }), []);
  const history = useAsync(() => wsAPI.listRequests({ type: 'project_code', scope: 'all' }), []);
  const all = projects.data?.projects || [];
  const clients = useMemo(() => [...new Set(all.map((p) => p.client).filter(Boolean))], [all]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    const h = setTimeout(() => wsAPI.codePreview({ businessUnit: f.businessUnit, division: f.division, financialYear: f.financialYear }).then((d) => setPreview(d.code)).catch(() => {}), 200);
    return () => clearTimeout(h);
  }, [f.businessUnit, f.division, f.financialYear]);

  const duplicate = useMemo(() => {
    if (!f.projectName.trim()) return null;
    const n = f.projectName.trim().toLowerCase();
    return all.find((p) => (p.name || '').toLowerCase() === n && (!f.client || (p.client || '').toLowerCase() === f.client.trim().toLowerCase()));
  }, [f.projectName, f.client, all]);

  const submit = async () => {
    const req = [['client', 'Client'], ['projectName', 'Project name'], ['site', 'Site'], ['projectManagerId', 'Project manager'], ['contractReference', 'Contract reference'], ['startDate', 'Start date'], ['plannedEndDate', 'Planned end date']];
    const miss = req.filter(([k]) => !f[k]).map(([, l]) => l);
    if (f.costCenterMapping === 'Required' && !f.costCenter) miss.push('Cost center');
    if (miss.length) { notify.error(`Please fill: ${miss.join(', ')}`); return; }
    if (new Date(f.plannedEndDate) < new Date(f.startDate)) { notify.error('Planned end date is before the start date'); return; }
    if (f.duplicateCheck === 'Enabled' && duplicate) { notify.error(`Duplicate: ${duplicate.projectCode} already exists for this client and name`); return; }
    setSaving(true);
    try {
      const pm = people.find((u) => u._id === f.projectManagerId);
      const cc = costCenters.find((c) => c.code === f.costCenter);
      const r = await wsAPI.createRequest({
        type: 'project_code', title: f.projectName, businessUnit: f.businessUnit, costCenter: f.costCenter || undefined, site: f.site,
        data: { ...f, projectManagerName: pm?.userName, costCenterName: cc?.name, proposedCode: preview, description: `${f.projectName} — ${f.client}` },
        submit: true,
      });
      notify.success(`${r.request.refNo} submitted for master-data approval`);
      setF(blank());
      history.reload();
      setTab('history');
    } catch (e) { notify.error(e.message || 'Could not submit'); } finally { setSaving(false); }
  };

  const toggleStatus = async (p) => {
    try {
      await vendorAPI.updateProject(p._id, { status: p.status === 'ACTIVE' ? 'CLOSED' : 'ACTIVE' });
      notify.success(`${p.projectCode} ${p.status === 'ACTIVE' ? 'closed' : 'reactivated'}`);
      clearCache('projects');
      projects.reload();
    } catch (e) { notify.error(e.message); }
  };

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return !t ? all : all.filter((p) => [p.projectCode, p.name, p.client, p.site, p.financialYear].some((v) => String(v || '').toLowerCase().includes(t)));
  }, [all, q]);
  const sort = useSort(filtered, 'createdAt', 'desc');
  const paged = usePaged(sort.rows, 12);

  const table = (rows, showActions) => (
    <div className="tse-table-wrap"><table className="tse-table">
      <thead><tr><Th sort={showActions ? sort : null} k="projectCode">Project code</Th><Th sort={showActions ? sort : null} k="name">Project name</Th><th>Client</th><th>Site</th><Th sort={showActions ? sort : null} k="financialYear">Financial year</Th><th>Status</th><Th sort={showActions ? sort : null} k="createdAt">Created on</Th><th>Created by</th>{showActions && canManage && <th />}</tr></thead>
      <tbody>{rows.map((p) => (
        <tr key={p._id}>
          <td className="strong">{p.projectCode}</td><td>{p.name}</td><td>{p.client || '—'}</td><td>{p.site || '—'}</td><td>{p.financialYear || '—'}</td>
          <td><Badge tone={p.status === 'ACTIVE' ? 'approved' : 'gray'} pill>{p.status === 'ACTIVE' ? 'Active' : 'Closed'}</Badge></td>
          <td>{fmtDate(p.createdAt)}</td><td>{p.createdByEmail || p.projectManagerName || '—'}</td>
          {showActions && canManage && <td><button type="button" className="tse-btn xs outline" onClick={() => toggleStatus(p)}>{p.status === 'ACTIVE' ? 'Close' : 'Reactivate'}</button></td>}
        </tr>
      ))}</tbody>
    </table></div>
  );

  return (
    <PageShell section="Project Admin">
      <PageHead title="Project Code Generation" subtitle="Create and manage project codes for TSE projects" />
      <div className="mb-18"><Tabs variant="card-tabs" value={tab} onChange={setTab} tabs={[{ key: 'register', label: 'Project register', count: all.length }, { key: 'create', label: 'Create project' }, { key: 'history', label: 'Approval history', count: (history.data?.requests || []).filter((r) => r.status === 'PENDING').length || undefined }]} /></div>

      {tab === 'create' && (
        <>
          <div className="tse-split mb-18" style={{ gridTemplateColumns: 'minmax(0, 1fr) 500px' }}>
            <Card title={<span style={{ fontSize: '1.053rem' }}>Create project</span>}>
              <div className="tse-form-grid c2">
                <Field label="Business unit" required><Select value={f.businessUnit} onChange={(e) => set('businessUnit', e.target.value)} options={BUSINESS_UNITS} /></Field>
                <Field label="Division" required><Select value={f.division} onChange={(e) => set('division', e.target.value)} options={DIVISIONS} /></Field>
                <Field label="Client" required>
                  <Input list="pc-clients" value={f.client} onChange={(e) => set('client', e.target.value)} placeholder="Select or type client" />
                  <datalist id="pc-clients">{clients.map((c) => <option key={c} value={c} />)}</datalist>
                </Field>
                <Field label="Project name" required error={f.duplicateCheck === 'Enabled' && duplicate ? `Matches existing ${duplicate.projectCode}` : undefined}><Input value={f.projectName} onChange={(e) => set('projectName', e.target.value)} placeholder="e.g. 200 KLD STP – Sample Project" /></Field>
                <Field label="Site" required><Select value={f.site} onChange={(e) => set('site', e.target.value)} options={[...new Set([f.site, ...LOCATIONS].filter(Boolean))]} /></Field>
                <Field label="Financial year" required><Select value={f.financialYear} onChange={(e) => set('financialYear', e.target.value)} options={FY} /></Field>
                <Field label="Project manager" required><Select value={f.projectManagerId} onChange={(e) => set('projectManagerId', e.target.value)} placeholder="Select employee" options={people.map((u) => ({ value: u._id, label: `${u.userName} · ${u.designation || ''}` }))} /></Field>
                <Field label="Contract reference" required><Input value={f.contractReference} onChange={(e) => set('contractReference', e.target.value)} placeholder="e.g. PO-DEMO-026" /></Field>
                <Field label="Start date" required><Input type="date" value={f.startDate} onChange={(e) => set('startDate', e.target.value)} /></Field>
                <Field label="Planned end date" required><Input type="date" min={f.startDate || undefined} value={f.plannedEndDate} onChange={(e) => set('plannedEndDate', e.target.value)} /></Field>
              </div>
            </Card>
            <Card title={<span style={{ fontSize: '1.032rem' }}>Project code preview</span>} actions={<Badge tone="info" lg>Proposed format</Badge>}>
              <div style={{ background: '#e8f2fb', borderRadius: 8, padding: '11px 13px', fontSize: '1.56rem', fontWeight: 700, color: '#173b5f', letterSpacing: '-0.5px' }}>{preview || '—'}</div>
              <Note tone="info" style={{ marginTop: 8 }}>Existing codes {all.slice(-2).map((p) => p.projectCode).join(' and ') || '25001-A and 25001-B'} remain unchanged.</Note>
              <div className="tse-strong" style={{ fontSize: '0.989rem', margin: '13px 0 7px' }}>Additional settings</div>
              <div className="tse-stack" style={{ gap: 8 }}>
                <Field label="Duplicate check" inline><Select value={f.duplicateCheck} onChange={(e) => set('duplicateCheck', e.target.value)} options={['Enabled', 'Disabled']} /></Field>
                <Field label="Cost center mapping" required inline><Select value={f.costCenterMapping} onChange={(e) => set('costCenterMapping', e.target.value)} options={['Required', 'Optional']} /></Field>
                {f.costCenterMapping === 'Required' && <Field label="Cost center" inline><Select value={f.costCenter} onChange={(e) => set('costCenter', e.target.value)} placeholder="Select cost center" options={costCenters.filter((c) => c.status === 'Active').map((c) => ({ value: c.code, label: `${c.code} — ${c.name}` }))} /></Field>}
                <Field label="Budget owner" inline><Select value={f.budgetOwner} onChange={(e) => set('budgetOwner', e.target.value)} options={['Assigned', 'Project manager', 'Business head', 'Finance']} /></Field>
              </div>
              <button type="button" className="tse-btn lg block" style={{ marginTop: 8 }} onClick={submit} disabled={saving}>{saving ? <Spin /> : null}Submit for master-data approval <i className="bi bi-chevron-right" /></button>
              <div className="tse-small tse-muted" style={{ textAlign: 'center', marginTop: 8 }}>Code reserved on approval &nbsp;•&nbsp; Audit trail retained.</div>
            </Card>
          </div>
          <Card title="Recent projects" actions={<button type="button" className="link" onClick={() => setTab('register')}>View all <i className="bi bi-arrow-right" /></button>} bodyClass="flush">
            {projects.loading ? <Loading /> : table(all.slice(0, 5), false)}
          </Card>
        </>
      )}

      {tab === 'register' && (
        <Card bodyClass="flush">
          <div className="tse-toolbar">
            <div className="tse-search"><i className="bi bi-search" /><input placeholder="Search code, name, client, site" value={q} onChange={(e) => setQ(e.target.value)} /></div>
            <div className="tse-spacer" />
            <button type="button" className="tse-btn outline sm" onClick={() => downloadCSV('project-register.csv', sort.rows, [{ label: 'Project code', value: 'projectCode' }, { label: 'Project name', value: 'name' }, { label: 'Client', value: 'client' }, { label: 'Site', value: 'site' }, { label: 'FY', value: 'financialYear' }, { label: 'Status', value: 'status' }, { label: 'Created', value: (p) => fmtDate(p.createdAt) }])}><i className="bi bi-download" />Export</button>
          </div>
          {projects.loading ? <Loading /> : !filtered.length ? <Empty title="No projects" /> : table(paged.rows, true)}
          <Pager {...paged} label="projects" />
        </Card>
      )}

      {tab === 'history' && (
        <Card bodyClass="flush">
          {history.loading ? <Loading /> : !(history.data?.requests || []).length ? <Empty icon="bi-clock-history" title="No project code requests yet" /> : (
            <div className="tse-table-wrap"><table className="tse-table">
              <thead><tr><th>Request</th><th>Project name</th><th>Client</th><th>Proposed / generated code</th><th>Requested by</th><th>Status</th><th>Updated</th><th /></tr></thead>
              <tbody>{history.data.requests.map((r) => (
                <tr key={r._id} className="clickable" onClick={() => setDetail(r._id)}>
                  <td className="strong">{r.refNo}</td><td>{r.title}</td><td>{r.data?.client}</td>
                  <td>{r.data?.generatedCode ? <Badge tone="approved">{r.data.generatedCode}</Badge> : <span className="tse-muted">{r.data?.proposedCode}</span>}</td>
                  <td>{r.requesterName}</td><td><ReqStatus request={r} /></td><td>{fmtDate(r.updatedAt)}</td>
                  <td><button type="button" className="tse-btn xs outline">{r.canAct ? 'Review' : 'Details'}</button></td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </Card>
      )}
      <RequestDetail id={detail} onClose={() => setDetail(null)} onChanged={() => { history.reload(); clearCache('projects'); projects.reload(); }} />
    </PageShell>
  );
}
